import { useMemo, useCallback } from 'react';
import cls from 'classnames';
import { useTable, usePagination, useSortBy, useRowSelect } from 'react-table';
import { Pagination } from 'react-bootstrap';
import { useUpdateEffect } from 'src/utils/hooks';
import classes from './Table.module.scss';

const defaultPropGetter = () => ({});

export const Table = ({ columns, data, fetchData, onSelectedRowsChange, tableOptions, ...props }) => {
  const table = useTable({
    columns, data,
    initialState: { pageIndex: props.pageIndex, pageSize: props.pageSize, sortBy: [props.sortBy ?? {}] },
    manualPagination: true,
    manualSortBy: true, disableMultiSort: true,
    pageCount: props.pageCount,
    ...tableOptions,
    useControlledState: (state) => useMemo(() => ({
      ...state, selectedRowIds: props.selectedRowIds ?? {}
    }), [state, props.selectedRowIds]),
  }, useSortBy, usePagination, useRowSelect);

  const {
    getColumnProps = defaultPropGetter,
    getCellProps = defaultPropGetter,
    getRowProps = defaultPropGetter,
    selectRow,
    renderFilterRow,
    filterRowClassName,
  } = props;

  const { pageIndex, pageSize, sortBy: sortByArray = [], selectedRowIds } = table.state;
  const sortBy = sortByArray.length ? sortByArray[0] : {};

  const getSelectedRowProps = useCallback((row) => {
    return !onSelectedRowsChange ? {} : {
      onClick: () => {
        row.toggleRowSelected();
        if (!row.isSelected) { selectedRowIds[row.id] = selectRow ? selectRow(row.original) : true }
        else { delete selectedRowIds[row.id] }

        onSelectedRowsChange && onSelectedRowsChange({ ...selectedRowIds });
      }
    }
  }, [onSelectedRowsChange, selectedRowIds]);

  useUpdateEffect(() => { props.pageIndex === 0 && pageIndex !== 0 && table.gotoPage(0) }, [props.pageIndex]);
  useUpdateEffect(() => { fetchData({ pageIndex, pageSize, sortBy }) }, [pageIndex, pageSize, sortBy.id, sortBy.desc]);

  return (
    <div className={classes.root}>

      <table {...table.getTableProps()} className="table table-hover">
        <thead>
          {table.headerGroups.map(headerGroup => {
            const { key, ...props } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} {...props}>
                {headerGroup.headers.map(column => {
                  const { key, ...props } = column.getHeaderProps([
                    { className: column.headerClassName, style: column.headerStyle },
                    column.getSortByToggleProps({ title: undefined }),
                  ]);
                  return (
                    <th key={key} {...props} >
                      {column.render('Header')}
                      {column.isSorted && <i className={cls('bi', { 'bi-caret-down-fill': column.isSortedDesc, 'bi-caret-up-fill': !column.isSortedDesc })}></i>}
                    </th>
                  )
                })}
              </tr>
            )
          })}
          {renderFilterRow && (
            <tr className={filterRowClassName}>
              {renderFilterRow(table.headerGroups?.[0]?.headers ?? [])}
            </tr>
          )}
        </thead>

        <tbody {...table.getTableBodyProps()}>
          {table.page.map(row => {
            table.prepareRow(row);
            const { key, ...props } = row.getRowProps([getRowProps(row), getSelectedRowProps(row)]);
            return (
              <tr key={key} {...props}>
                {row.cells.map(cell => {
                  const { key, ...props } = cell.getCellProps([
                    { className: cell.column.className, style: cell.column.style },
                    getColumnProps(cell.column), getCellProps(cell)
                  ]);
                  return (
                    <td key={key} {...props}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>

      <TablePagination {...table} pageIndex={props.pageIndex} />
    </div>
  )
}

const TablePagination = ({ pageIndex, pageCount, gotoPage, canNextPage, canPreviousPage, previousPage, nextPage }) => {
  const maxVisible = 7; // Maximum number of page items to show (excluding first/prev/next/last)

  const getVisiblePages = useMemo(() => {
    if (pageCount <= maxVisible) {
      // Show all pages if total is less than or equal to maxVisible
      return Array.from({ length: pageCount }, (_, i) => i);
    }

    const pages = [];
    const sideCount = 2; // Number of pages to show on each side of current page
    
    // Determine if we should show first and last pages
    const showFirst = pageIndex > sideCount;
    const showLast = pageIndex < pageCount - sideCount - 1;

    if (showFirst) {
      pages.push(0);
      if (pageIndex > sideCount + 1) {
        pages.push('ellipsis-start');
      }
    }

    // Calculate the range of pages to show around current page
    let start = showFirst ? Math.max(1, pageIndex - sideCount) : 0;
    let end = showLast ? Math.min(pageCount - 2, pageIndex + sideCount) : pageCount - 1;

    // Ensure we show at least the current page
    if (start > pageIndex) start = pageIndex;
    if (end < pageIndex) end = pageIndex;

    // Add the page range
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (showLast) {
      if (pageIndex < pageCount - sideCount - 2) {
        pages.push('ellipsis-end');
      }
      pages.push(pageCount - 1);
    }

    return pages;
  }, [pageIndex, pageCount, maxVisible]);

  return (
    <>
      {pageCount > 1 &&
        <Pagination>
          <Pagination.First disabled={pageIndex === 0} onClick={() => gotoPage(0)} />
          <Pagination.Prev disabled={!canPreviousPage} onClick={previousPage} />

          {getVisiblePages.map((page, index) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <Pagination.Item key={`ellipsis-${index}`} disabled>
                  ...
                </Pagination.Item>
              );
            }
            return (
              <Pagination.Item
                key={page}
                active={pageIndex === page}
                onClick={() => gotoPage(page)}
              >
                {page + 1}
              </Pagination.Item>
            );
          })}

          <Pagination.Next disabled={!canNextPage} onClick={nextPage} />
          <Pagination.Last disabled={(pageIndex + 1) === pageCount} onClick={() => gotoPage(pageCount - 1)} />
        </Pagination>
      }
    </>
  );
}
